/**
 * Shared SVG icon set for buttons across every screen. We never use OS emoji as
 * button glyphs — they render differently per device/font and can't be recoloured
 * or sized reliably. Every icon here is line/solid art that inherits the button's
 * text colour via `currentColor`, so one glyph looks right on brass, navy, etc.
 *
 * Line icons use a shared stroke style; a few (play/pause/next) are solid.
 */
const STK = 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
// Default to a 1em box + slight baseline drop so an icon dropped inline into a
// heading or log line sizes/aligns with the surrounding text. Buttons that set an
// explicit width/height in CSS override this (CSS beats the presentation attrs).
const svg = (inner: string, solid = false): string =>
  `<svg viewBox="0 0 24 24" width="1em" height="1em" style="vertical-align:-0.14em" ${solid ? 'fill="currentColor"' : STK} aria-hidden="true">${inner}</svg>`;

// ---- naval / action glyphs ----
export const ICON_ANCHOR = svg('<circle cx="12" cy="4" r="2"/><path d="M12 6v15"/><path d="M8 10h8"/><path d="M5 13a7 7 0 0 0 14 0"/>');
export const ICON_FLAG = svg('<path d="M6 3v18"/><path d="M6 4h12l-3 4 3 4H6z" fill="currentColor" stroke="none"/>');
export const ICON_DIVE = svg('<path d="M3 7h18"/><path d="M12 10v9"/><path d="M7.5 14.5 12 19l4.5-4.5"/>');
export const ICON_SURFACE = svg('<path d="M3 17h18"/><path d="M12 14V5"/><path d="M7.5 9.5 12 5l4.5 4.5"/>');

// ---- undo / redo (curved arrows) ----
export const ICON_UNDO = svg('<path d="M8 8 4 12l4 4"/><path d="M4 12h9a6 6 0 0 1 0 12h-2"/>');
export const ICON_REDO = svg('<path d="M16 8l4 4-4 4"/><path d="M20 12h-9a6 6 0 0 0 0 12h2"/>');

// ---- media / navigation glyphs (solid) ----
export const ICON_NEXT = svg('<path d="M5 4l10 8-10 8z"/><rect x="16.4" y="4" width="2.9" height="16" rx="1.3"/>', true);
export const ICON_PLAY = svg('<path d="M6 3l14 9-14 9z"/>', true);
export const ICON_PAUSE = svg('<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>', true);

// ---- title-menu glyphs ----
// New Battle — crossed swords (Lucide "swords").
export const ICON_SWORDS = svg(
  '<polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" y1="19" x2="19" y2="13"/>'
  + '<line x1="16" y1="16" x2="20" y2="20"/><line x1="19" y1="21" x2="21" y2="19"/>'
  + '<polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/><line x1="5" y1="14" x2="9" y2="18"/>'
  + '<line x1="7" y1="17" x2="4" y2="20"/><line x1="3" y1="19" x2="5" y2="21"/>',
);
// Campaign / minimap — a folded map (Lucide "map").
export const ICON_MAP = svg(
  '<path d="M9 18 3 21V6l6-3 6 3 6-3v15l-6 3-6-3z"/><path d="M9 3v15"/><path d="M15 6v15"/>',
);
// Instant Battles — a lightning bolt = "instant" (Lucide "zap").
export const ICON_ZAP = svg('<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>');
// Tutorial — a graduation cap (Lucide "graduation-cap").
export const ICON_CAP = svg('<path d="M22 10 12 5 2 10l10 5 10-5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/><path d="M22 10v6"/>');
// Settings — a gear (Lucide "settings").
export const ICON_GEAR = svg(
  '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>'
  + '<circle cx="12" cy="12" r="3"/>',
);
// Credits — a scroll (Lucide "scroll").
export const ICON_SCROLL = svg(
  '<path d="M19 17V5a2 2 0 0 0-2-2H4"/>'
  + '<path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/>',
);

// ---- status / text glyphs ----
// Campaign complete — a trophy (Lucide "trophy").
export const ICON_TROPHY = svg(
  '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>'
  + '<path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>'
  + '<path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>',
);
// Build / shipyard — a solid hammer (head + handle), filled like the Fleet flag
// pennant so the two tabs share a weight instead of one being a thin outline.
export const ICON_HAMMER = svg(
  '<rect x="3.5" y="4" width="17" height="5" rx="1.6"/>'
  + '<rect x="10.2" y="8.4" width="3.6" height="12.4" rx="1.5"/>',
  true,
);
// Arrived / done — a checkmark.
export const ICON_CHECK = svg('<path d="M5 12.5 10 17.5 19.5 6.5"/>');
// Close — an X.
export const ICON_CLOSE = svg('<path d="M5 5 19 19M19 5 5 19"/>');
// Add / set order — a plus. Pairs with ICON_CLOSE on the fleet-order rows (set a
// target with +, cancel it with ×).
export const ICON_ADD = svg('<path d="M12 5v14M5 12h14"/>');
// In-game currency mark — a 4-point mariner's star. The single points pool funds
// ships AND decides the battle; this replaces the old "$" and "pts". Solid + slightly
// inset in the box so it sits at digit height when placed inline before a number.
export const ICON_VP = svg('<path d="M12 3 13.6 10.4 21 12 13.6 13.6 12 21 10.4 13.6 3 12 10.4 10.4Z"/>', true);
// Course arrow (home → destination) between the buy-screen port pickers. Mimics the
// map's course trail: a filled ORIGIN DOT + a line + an arrowhead. A shape, not a
// text glyph, so it renders identically everywhere; rotate it in CSS to point down
// (dot on top) when the pickers stack vertically.
export const ICON_ARROW = '<svg viewBox="0 0 24 24" width="1em" height="1em" style="vertical-align:-0.14em" aria-hidden="true">'
  + '<circle cx="3.4" cy="12" r="2.5" fill="currentColor"/>'
  + '<path d="M6.2 12 H13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
  + '<path d="M12.5 7 L20 12 L12.5 17 Z" fill="currentColor"/>'
  + '</svg>';
// The settings-window close glyph (a slightly inset X). Shared so the fleet/orders
// window close button matches the settings close button exactly.
export const ICON_X = '<svg viewBox="0 0 12 12" width="1em" height="1em" style="vertical-align:-0.14em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3.2 3.2 8.8 8.8M8.8 3.2 3.2 8.8"/></svg>';

// ---- audio glyphs (Lucide "volume-2" / "volume-x") ----
export const ICON_VOLUME = svg('<path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a10 10 0 0 1 0 14"/>');
export const ICON_VOLUME_OFF = svg('<path d="M11 5 6 9H2v6h4l5 4z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/>');
